import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { InfiniteScroll } from "stream-chat-react";
import CallTranslatedMessage from "./CallTranslatedMessage";
import { ReactComponent as TapToSpeakIcon } from "../../Assets/icons/callControlButtons/tapToSpeak.svg";
import { ReactComponent as StopSpeakingIcon } from "../../Assets/icons/callControlButtons/stopSpeaking.svg";
import { ReactComponent as WaitToSpeak } from "../../Assets/icons/callControlButtons/waitToSpeak.svg";

import { useAuthService } from "../../Context/AuthContext";
import { useNotify } from "../../Helpers/Notifications";
import PrimaryButton from "../GenericComponents/PrimaryButton";
import { SEQUENCIAL_TRANSLATION, REAL_TIME_TRANSLATION } from "../../Constants/constantValues";
import { useTranslate } from '@tolgee/react';

const ENDED = "ended";
const REQUESTED = "requested";
const SPEAKING = "speaking";

export const useSpeakTurnQueue = ({
  speakersQueue,
  canRequestSpeak,
  translationType,
  speakStatus,
  setSpeakStatus,
  handleSendCancelSpeakMessage,
  handleRequestSpeakMessage,
  requestSpeakPayload,
  muteMicrophone,
  unmuteMicrophone,
  micEnabledRef,
  isLeavingCallRef,
  room,
  localParticipant,
}) => {
  const { notifyInfo } = useNotify();
  const { t } = useTranslate();
  const { user } = useAuthService();
  const userId = user?.id;

  const [ahedUsers, setAheadUsers] = useState(null);
  const [shouldWaitForSpeak] = useState(false);
  const timerPassedRef = useRef(false);
  const prevSpeakersQueue = useRef([]);
  const restoringMicRef = useRef(false);

  const calculateQueue = (speakersQueue) => {
    if (!userId) {
      setAheadUsers(null);
      return;
    }
    let foundIndex = speakersQueue.findIndex(
      (item) => item.user_id === userId
    );
    if (foundIndex > -1) {
      setAheadUsers(foundIndex);
    } else {
      setAheadUsers(null);
    }
  };

  const setSpeakTurn = () => {
    if (speakStatus === SPEAKING) {
      return;
    }
    unmuteMicrophone().then(() => {
      setSpeakStatus(SPEAKING);
    });
  };

  const setSpeakTurnIfNeeded = () => {
    if (!userId || !canRequestSpeak) {
      return;
    }
    if (speakersQueue[0]?.user_id != userId) {
      if (speakStatus === SPEAKING && speakersQueue.some((item) => String(item.user_id) === String(userId))) {
        muteMicrophone();
        setSpeakStatus(REQUESTED);
        notifyInfo(t('call_modal.speaking_paused_by_priority'));
      }
      return;
    }
    if (
      prevSpeakersQueue.current.length === 0 ||
      !shouldWaitForSpeak ||
      timerPassedRef.current
    ) {
      setSpeakTurn();
    }
  };

  const handleRequestSpeak = () => {
    if (!canRequestSpeak) {
      return;
    }
    handleRequestSpeakMessage(requestSpeakPayload);
    setSpeakStatus(REQUESTED);
  };

  const handleStopSpeaking = async () => {
    handleSendCancelSpeakMessage();
    setSpeakStatus(ENDED);
    muteMicrophone();
  };

  const handleStopSpeakingWithoutSend = async () => {
    handleSendCancelSpeakMessage();
    setSpeakStatus(ENDED);
    muteMicrophone();
  };

  useEffect(() => {
    restoringMicRef.current = false;

    const handleLocalTrackPublished = (publication, participant) => {
      if (participant.isLocal && publication.kind === "audio") {
        muteMicrophone();
      }
    };

    const resyncLocalTrackMuteIntent = (publication, participant) => {
      if (participant.isLocal && publication.kind === "audio" && micEnabledRef?.current) {
        publication.track?.mute();
      }
    };

    if (translationType === SEQUENCIAL_TRANSLATION && room) {
      const alreadyPublished =
        localParticipant.localParticipant.audioTrackPublications.size > 0;
      const restoreMuted = alreadyPublished && !!micEnabledRef?.current;
      if (alreadyPublished) {
        muteMicrophone();
      } else {
        room.on("localTrackPublished", handleLocalTrackPublished);
      }
      return () => {
        room.off("localTrackPublished", handleLocalTrackPublished);
        if (!restoreMuted) {
          restoringMicRef.current = true;
          unmuteMicrophone();
        }
      };
    }
    if (translationType === REAL_TIME_TRANSLATION && speakStatus !== ENDED) {
      handleSendCancelSpeakMessage();
    }
    if (translationType === REAL_TIME_TRANSLATION) {
      if (room) {
        room.on("localTrackPublished", resyncLocalTrackMuteIntent);
      }
    }
    if (speakStatus !== ENDED) {
      setSpeakStatus(ENDED);
    }
    return () => {
      if (translationType === REAL_TIME_TRANSLATION && room) {
        room.off("localTrackPublished", resyncLocalTrackMuteIntent);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [translationType]);

  useEffect(() => {
    calculateQueue(speakersQueue);
    setSpeakTurnIfNeeded();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canRequestSpeak, speakersQueue]);

  useEffect(() => {
    setSpeakTurnIfNeeded();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldWaitForSpeak]);

  useEffect(() => {
    return () => {
      handleSendCancelSpeakMessage();
      setSpeakStatus(ENDED);
      if (isLeavingCallRef.current === false) {
        if (micEnabledRef.current === true && !restoringMicRef.current) {
          notifyInfo(t('request_translation_modal.mic_info'));
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    ahedUsers,
    handleRequestSpeak,
    handleStopSpeaking,
    handleStopSpeakingWithoutSend,
  };
};

const TranslationFeedPanel = ({
  messages,
  speechRecongnitionMessages,
  useRealtimeApi = false,
  realtimeTranslations,
  isOverlay = false,
  compact = false,
}) => {
  const { t } = useTranslate();
  const { user, callTranslationsLanguages } = useAuthService();
  const userId = user?.id;
  const userCallLanguage = user?.call_language;

  const foundLanguage = callTranslationsLanguages?.find(
    (item) => item.locale === userCallLanguage
  );

  return (
    <MessagesList id="messageListContainer" $overlay={isOverlay} $compact={compact}>
      <InfiniteScroll
        dataLength={messages?.length}
        scrollableTarget="messageListContainer"
        inverse
        style={{
          display: "flex",
          flexDirection: "column-reverse",
          gap: "4px",
          width: "100%",
        }}
      >
        {speechRecongnitionMessages.length === 0 && (
          <InfoBlock>
            <p>
              {t('speech_container.note')}{" "}
              {foundLanguage?.name} ({foundLanguage?.translation}){" "}
              {t('speech_container.note2')}
            </p>
          </InfoBlock>
        )}
        {speechRecongnitionMessages.map((message, index) => (
          <CallTranslatedMessage
            faded={index !== 0}
            key={index}
            message={message}
            ownMessage={message.user_id === userId}
            useRealtimeApi={useRealtimeApi}
            realtimeTranslation={
              useRealtimeApi ? realtimeTranslations?.get(message?.id) : undefined
            }
          />
        ))}
      </InfiniteScroll>
    </MessagesList>
  );
};

export default TranslationFeedPanel;

export const SpeakTurnControl = ({
  translationType,
  canRequestSpeak = true,
  speakStatus,
  ahedUsers,
  handleRequestSpeak,
  handleStopSpeakingWithoutSend,
  handleStopSpeaking,
}) => {
  const { t } = useTranslate();

  if (translationType !== SEQUENCIAL_TRANSLATION || !canRequestSpeak) {
    return null;
  }

  return (
    <Bottom>
      {speakStatus === ENDED && (
        <PrimaryButton style={{ width: "100%" }} onClick={() => handleRequestSpeak()}>
          <TapToSpeakIcon /> {t('call_modal.tap_to_speak')}
        </PrimaryButton>
      )}
      {speakStatus === REQUESTED && (
        <WaitToSpeakBtn onClick={() => handleStopSpeakingWithoutSend()}>
          <WaitToSpeak />
          <TextWrapper>
            <Text>
              {t('call_modal.wait_to_speak')}{" "}
              {ahedUsers !== null && ahedUsers !== 0 && (
                <label>, {ahedUsers} {t('call_modal.ahead')}.</label>
              )}
            </Text>
            <CancelText>{t('call_modal.tap_to_cancel')}</CancelText>
          </TextWrapper>
        </WaitToSpeakBtn>
      )}
      {speakStatus === SPEAKING && (
        <FinishSpeaking onClick={() => handleStopSpeaking()}>
          <StopSpeakingIcon /> {t('call_modal.finish_speaking')}
        </FinishSpeaking>
      )}
    </Bottom>
  );
};

const MessagesList = styled.div`
  display: flex;
  flex-direction: column-reverse;
  height: ${({ $compact }) => $compact ? "auto" : "100%"};
  flex: ${({ $compact }) => $compact ? "1" : "initial"};
  min-height: ${({ $compact }) => $compact ? "0" : "auto"};
  border-radius: 4px;
  background: ${({ theme, $overlay }) => $overlay ? "transparent" : theme.callComponent.participantsListBackground};
  width: 100%;
  overflow: auto;
  padding: 10px 0;
`;

const InfoBlock = styled.div`
  p {
    font-size: 16px;
    color: ${({ theme }) => theme.menu.secondaryTextColor};
    padding: 20px;
  }
`;

const Bottom = styled.div`
  display: flex;
  width: 100%;
  min-height: 80px;
  padding: 18px 0 10px;
`;

const TapToSpeakBtn = styled.button`
  display: flex;
  width: 100%;
  padding: 18px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  font-weight: 500;
  color: ${({ theme }) => theme.menu.primaryTextColor};
  background: ${({ theme }) => theme.primaryButton.background};
  border: none;
  border-radius: 4px;
  cursor: pointer;

  svg {
    max-width: 12px;
    max-height: 24px;
  }
`;

const FinishSpeaking = styled(TapToSpeakBtn)`
  background: ${({ theme }) => theme.finishSpeakBtn.background};
  color: ${({ theme }) => theme.finishSpeakBtn.color};
`;

const WaitToSpeakBtn = styled(TapToSpeakBtn)`
  background: rgba(65, 64, 131, 1);
  svg {
    max-width: 20px;
    max-height: 24px;
  }
`;

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Text = styled.p`
  font-size: 14px;
  color: white;
  text-align: center;
  margin: 0;

  label {
    font-weight: 600;
  }
`;

const CancelText = styled.p`
  font-size: 12px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.62);
  text-align: center;
  margin: 0;
`;
